// The starting shapes a new list is made from, and the one conversion of a definition
// into what the server takes. A shape is only a starting point: its states and columns
// are copied into the dialog, edited freely, and saved as the list's own (migration 078).
// Labels come from i18n; the tone, the "finished" flag and the column kinds are here.
//
// The FIRST state of each shape is where a new row starts, so it is always the
// not-yet-handled one.
import he from '../../i18n/he';

const STRUCTURE = {
  invitees: { states: [['blue', false], ['green', true], ['amber', false], ['gray', true]], columns: ['phone', 'text', 'number'] },
  press: { states: [['gray', false], ['blue', false], ['amber', false], ['green', true], ['gray', true]], columns: ['text', 'phone', 'email'] },
  suppliers: { states: [['blue', false], ['green', true], ['gray', true]], columns: ['phone', 'text'] },
  deliveries: { states: [['gray', false], ['blue', false], ['green', true]], columns: ['text', 'phone'] },
  blank: { states: [['gray', false], ['blue', false], ['green', true]], columns: [] },
};

export const SHAPE_KEYS = Object.keys(STRUCTURE);
export const TONES = ['green', 'amber', 'blue', 'red', 'gray'];
export const KINDS = ['text', 'phone', 'email', 'date', 'number'];

let seq = 0;
// A row not yet saved has no id; `key` keeps React's list stable while it is edited.
export const localKey = () => { seq += 1; return `new-${seq}`; };

export const definitionOf = (shape) => {
  const text = he.lists.shapes[shape];
  return {
    states: STRUCTURE[shape].states.map(([tone, finished], i) => ({
      key: localKey(), label: text.states[i], tone, is_finished: finished,
    })),
    columns: STRUCTURE[shape].columns.map((kind, i) => ({ key: localKey(), label: text.columns[i], kind })),
  };
};

// A stored list's definition, ready to edit.
export const definitionFromList = (list) => ({
  states: list.states.map((s) => ({ key: `s${s.id}`, id: s.id, label: s.label, tone: s.tone, is_finished: !!s.is_finished })),
  columns: list.columns.map((c) => ({ key: `c${c.id}`, id: c.id, label: c.label, kind: c.kind })),
});

// A copy of another list's definition — the same states and columns, as new rows. Each
// column remembers the column it came from (`from`), so the source's rows can follow
// into the new list whatever the owner reorders or drops before saving.
export const definitionCopiedFrom = (list) => {
  const def = definitionFromList(list);
  return {
    states: def.states.map(({ id, ...s }) => ({ ...s, key: localKey() })),
    columns: def.columns.map(({ id, ...c }) => ({ ...c, from: id, key: localKey() })),
  };
};

// The source list's rows, as new rows of the list just created from its copy: names,
// notes and the values of every column that was kept. They all start in the first state —
// a copied guest list is a new round of asking, not last event's answers.
export const copiedRows = (sourceItems, draftColumns, createdList) => sourceItems.map((it) => ({
  name: it.name,
  note: it.note || undefined,
  cells: Object.fromEntries(draftColumns
    .map((c, i) => [createdList.columns[i]?.id, c.from ? it.cells[c.from] : undefined])
    .filter(([id, v]) => id && v)),
}));

export const toPayload = (def) => ({
  states: def.states.map(({ id, label, tone, is_finished }) => ({ id, label: label.trim(), tone, is_finished })),
  columns: def.columns.map(({ id, label, kind }) => ({ id, label: label.trim(), kind })),
});

export const isComplete = (def) =>
  def.states.length > 0 && def.states.every((s) => s.label.trim()) && def.columns.every((c) => c.label.trim());
